<template>
  <div class="ceo-dashboard">
    <ModalLoading :isLoading="isLoading" />

    <!-- KPI Cards -->
    <div class="kpi-grid">
      <!-- Ventas del Mes -->
      <div class="kpi-card kpi-primary">
        <div class="kpi-icon"><i class="bi bi-graph-up-arrow"></i></div>
        <div class="kpi-body">
          <span class="kpi-label">Ventas del Mes</span>
          <span class="kpi-value">L. {{ formatMonto(ventasMes.mes_actual) }}</span>
          <span class="kpi-badge" :class="trendClass(ventasMes.cambio_pct)" v-if="ventasMes.cambio_pct !== null">
            <i :class="ventasMes.cambio_pct >= 0 ? 'bi bi-arrow-up-short' : 'bi bi-arrow-down-short'"></i>
            {{ Math.abs(ventasMes.cambio_pct) }}% vs mes anterior
          </span>
          <span class="kpi-sub" v-else>Sin datos del mes anterior</span>
        </div>
        <router-link to="/administrar-ventas" class="kpi-link">Ver ventas →</router-link>
      </div>

      <!-- Ventas Hoy -->
      <div class="kpi-card">
        <div class="kpi-icon"><i class="bi bi-cash-coin"></i></div>
        <div class="kpi-body">
          <span class="kpi-label">Ventas Hoy</span>
          <span class="kpi-value">L. {{ formatMonto(ventasComparativo?.hoy ?? 0) }}</span>
          <span class="kpi-badge" :class="trendClass(ventasComparativo?.cambio_pct)" v-if="ventasComparativo?.cambio_pct !== null && ventasComparativo?.cambio_pct !== undefined">
            <i :class="ventasComparativo.cambio_pct >= 0 ? 'bi bi-arrow-up-short' : 'bi bi-arrow-down-short'"></i>
            {{ Math.abs(ventasComparativo.cambio_pct) }}% vs ayer
          </span>
        </div>
        <router-link to="/administrar-ventas" class="kpi-link">Ver detalle →</router-link>
      </div>

      <!-- CxC Pendiente -->
      <div class="kpi-card kpi-warning" v-if="cxcTotal.total_pendiente > 0 || cxcTotal.cantidad >= 0">
        <div class="kpi-icon"><i class="bi bi-receipt-cutoff"></i></div>
        <div class="kpi-body">
          <span class="kpi-label">Cuentas por Cobrar</span>
          <span class="kpi-value">L. {{ formatMonto(cxcTotal.total_pendiente) }}</span>
          <span class="kpi-sub">{{ cxcTotal.cantidad }} cuenta{{ cxcTotal.cantidad !== 1 ? 's' : '' }} pendiente{{ cxcTotal.cantidad !== 1 ? 's' : '' }}</span>
        </div>
        <router-link to="/cuentas-por-cobrar" class="kpi-link">Ver cuentas →</router-link>
      </div>

      <!-- Clientes -->
      <div class="kpi-card">
        <div class="kpi-icon"><i class="bi bi-people-fill"></i></div>
        <div class="kpi-body">
          <span class="kpi-label">Clientes Activos</span>
          <span class="kpi-value">{{ clientesNumero }}</span>
          <span class="kpi-sub">registrados en la empresa</span>
        </div>
        <router-link to="/clientes" class="kpi-link">Ver clientes →</router-link>
      </div>

      <!-- Cajas -->
      <div class="kpi-card">
        <div class="kpi-icon"><i class="bi bi-cash-stack"></i></div>
        <div class="kpi-body">
          <span class="kpi-label">Estado de Cajas</span>
          <span class="caja-badge" :class="cajaBadge.cls">{{ cajaBadge.label }}</span>
          <span class="kpi-sub" v-if="cajaEstado">
            {{ cajaEstado.cajas_abiertas || 0 }} {{ cajaEstado.cajas_abiertas === 1 ? 'caja abierta' : 'cajas abiertas' }}
          </span>
        </div>
        <router-link to="/arqueo-caja" class="kpi-link">Ver cajas →</router-link>
      </div>

      <!-- Alertas Inventario -->
      <div class="kpi-card" :class="{ 'kpi-danger': alertasUrgentes > 0 }">
        <div class="kpi-icon"><i class="bi bi-exclamation-triangle-fill"></i></div>
        <div class="kpi-body">
          <span class="kpi-label">Inventario</span>
          <div class="alert-pills">
            <span class="pill pill-ok">{{ alertasNormales }} normal</span>
            <span class="pill pill-warn">{{ alertasAtencion }} atención</span>
            <span class="pill pill-err">{{ alertasUrgentes }} urgente</span>
          </div>
          <span class="kpi-sub stock-critico" v-if="stockCritico > 0">
            <i class="bi bi-exclamation-circle-fill"></i> {{ stockCritico }} producto{{ stockCritico !== 1 ? 's' : '' }} en stock crítico
          </span>
        </div>
        <router-link to="/admin-invenario" class="kpi-link">Ver alertas →</router-link>
      </div>
    </div>

    <!-- Top 5 Productos + Gráfico 3 meses -->
    <div class="mid-section">
      <!-- Top productos general -->
      <div class="panel top-panel">
        <div class="panel-header">
          <i class="bi bi-trophy-fill"></i> Top Productos
          <span class="top-col-label">vendidas</span>
        </div>
        <div v-if="topProductosGeneral.length > 0" class="top-list">
          <div v-for="(p, i) in topProductosGeneral" :key="i" class="top-item">
            <span class="rank" :class="{ 'rank-gold': i === 0, 'rank-silver': i === 1, 'rank-bronze': i === 2 }">#{{ i + 1 }}</span>
            <span class="prod-nombre">{{ p.nombre }}</span>
            <span class="prod-uds">{{ p.unidades }} uds</span>
          </div>
        </div>
        <div v-else class="no-data">
          <i class="bi bi-box-seam"></i>
          <p>Sin ventas registradas</p>
        </div>
      </div>

      <!-- Gráfico ventas 3 meses -->
      <div class="panel chart-panel">
        <div class="panel-header">
          <i class="bi bi-calendar3"></i> Ventas últimos 3 meses
        </div>
        <div class="chart-wrap">
          <LineChart :chart-data="lineChartData" :chart-options="lineChartOptions" :styles="chartStyles" />
        </div>
      </div>
    </div>

    <!-- Últimas ventas -->
    <div class="panel sales-panel">
      <div class="panel-header">
        <i class="bi bi-clock-history"></i> Últimas Ventas (todas las sucursales)
      </div>
      <div class="table-wrap">
        <table class="sales-table" v-if="sales.length > 0">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Vendedor</th>
              <th>Teléfono</th>
              <th>Fecha</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(s, i) in sales" :key="i">
              <td>{{ s.nombre }}</td>
              <td>{{ s.vendedor }}</td>
              <td>{{ s.numero }}</td>
              <td>{{ formatFecha(s.fecha) }}</td>
              <td class="monto-col">{{ s.total }}</td>
            </tr>
          </tbody>
        </table>
        <div class="no-data" v-else>
          <i class="bi bi-receipt"></i>
          <p>Sin ventas registradas</p>
        </div>
      </div>
      <router-link to="/administrar-ventas" class="ver-todas-link">Ver todas las ventas →</router-link>
    </div>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';
import ModalLoading from '@/components/ModalLoading.vue';
import solicitudes from '../../services/solicitudes.js';
import { getAlerts } from '../../services/alertasSolicitudes.js';
import {
  getVentasComparativo, getCajaEstado, getStockCritico,
  getVentasUltimosTresMeses, getUltimasVentas, getClientesPorEmpresa,
  getVentasMes, getTotalCxCEmpresa, getTopProductosGeneral,
} from '../../services/dashboardSolicitudes.js';

import {
  Chart as ChartJS, Title, Tooltip, Legend,
  LineElement, PointElement, CategoryScale, LinearScale,
} from 'chart.js';
ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale);

export default {
  name: 'DashboardCEO',
  components: { LineChart: Line, ModalLoading },

  data() {
    return {
      isLoading: false,
      id_usuario: null,
      refreshTimer: null,

      ventasMes:        { mes_actual: 0, mes_anterior: 0, cambio_pct: null },
      ventasComparativo: null,
      cxcTotal:         { total_pendiente: 0, cantidad: 0 },
      clientesNumero:   0,
      cajaEstado:       null,
      stockCritico:          0,
      topProductosGeneral:   [],
      alertasUrgentes:       0,
      alertasAtencion:  0,
      alertasNormales:  0,
      sales:            [],

      chartStyles: { height: '100%', width: '100%', position: 'relative' },
      lineChartData: {
        labels: [],
        datasets: [
          { label: 'Ventas Pagadas',    borderColor: '#16A34A', backgroundColor: 'rgba(22,163,74,0.08)', data: [], fill: true, tension: 0.3 },
          { label: 'Ventas Canceladas', borderColor: '#DC2626', backgroundColor: 'rgba(220,38,38,0.08)',  data: [], fill: true, tension: 0.3 },
        ],
      },
      lineChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: { y: { beginAtZero: true, ticks: { stepSize: 1, callback: v => Math.floor(v) } } },
      },
    };
  },

  computed: {
    cajaBadge() {
      if (!this.cajaEstado) return { label: 'Sin datos', cls: 'badge-grey' };
      if (this.cajaEstado.abierta) return { label: 'Abierta', cls: 'badge-green' };
      return { label: 'Cerrada', cls: 'badge-red' };
    },
  },

  methods: {
    formatMonto(v) {
      return Number(v || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatFecha(f) {
      return new Date(f).toLocaleString('es-HN', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', hour12: false });
    },
    trendClass(pct) {
      if (pct === null || pct === undefined) return '';
      return pct >= 0 ? 'trend-up' : 'trend-down';
    },

    async cargarKpis() {
      await Promise.all([
        this.cargarVentasMes(),
        this.cargarVentasComparativo(),
        this.cargarCxC(),
        this.cargarClientes(),
        this.cargarCajaEstado(),
        this.cargarStockCritico(),
        this.cargarTopProductosGeneral(),
        this.cargarAlertas(),
        this.cargarUltimasVentas(),
        this.cargarGrafico(),
      ]);
    },

    async cargarVentasMes() {
      try {
        const r = await getVentasMes(this.id_usuario);
        if (r) this.ventasMes = r;
      } catch (e) { console.error('ventas-mes:', e); }
    },
    async cargarVentasComparativo() {
      try {
        const r = await getVentasComparativo(this.id_usuario);
        if (r) this.ventasComparativo = r;
      } catch (e) { console.error('comparativo:', e); }
    },
    async cargarCxC() {
      try {
        const r = await getTotalCxCEmpresa(this.id_usuario);
        if (r) this.cxcTotal = r;
      } catch (e) { console.error('cxc-total:', e); }
    },
    async cargarClientes() {
      try {
        const r = await getClientesPorEmpresa(this.id_usuario);
        if (r) this.clientesNumero = r.totalClientes || 0;
      } catch (e) { console.error('clientes:', e); }
    },
    async cargarCajaEstado() {
      try {
        const r = await getCajaEstado(this.id_usuario);
        if (r) this.cajaEstado = r;
      } catch (e) { console.error('caja-estado:', e); }
    },
    async cargarStockCritico() {
      try {
        const r = await getStockCritico(this.id_usuario);
        if (r) this.stockCritico = r.count || 0;
      } catch (e) { console.error('stock-critico:', e); }
    },
    async cargarTopProductosGeneral() {
      try {
        const r = await getTopProductosGeneral(this.id_usuario);
        if (Array.isArray(r)) this.topProductosGeneral = r;
      } catch (e) { console.error('top-productos-general:', e); }
    },
    async cargarAlertas() {
      try {
        const alertas = await getAlerts(this.id_usuario);
        this.alertasUrgentes  = alertas.filter(a => a.puntaje >= 80).length;
        this.alertasAtencion  = alertas.filter(a => a.puntaje >= 45 && a.puntaje < 80).length;
        this.alertasNormales  = alertas.filter(a => a.puntaje < 45).length;
      } catch (e) { console.error('alertas:', e); }
    },
    async cargarUltimasVentas() {
      try {
        const r = await getUltimasVentas(this.id_usuario);
        if (r?.sales) this.sales = r.sales;
      } catch (e) { console.error('ultimas-ventas:', e); }
    },
    async cargarGrafico() {
      try {
        const r = await getVentasUltimosTresMeses(this.id_usuario);
        if (r?.chartData?.lineChartData) {
          const api = r.chartData.lineChartData;
          this.lineChartData = {
            labels: api.labels,
            datasets: this.lineChartData.datasets.map((ds, i) => ({
              ...ds, data: api.datasets[i]?.data ?? [],
            })),
          };
        }
      } catch (e) { console.error('grafico:', e); }
    },

    async refreshKpis() {
      try { await solicitudes.solicitarUsuarioToken(); } catch { clearInterval(this.refreshTimer); return; }
      await Promise.all([
        this.cargarVentasMes(), this.cargarVentasComparativo(),
        this.cargarCxC(), this.cargarCajaEstado(),
        this.cargarStockCritico(), this.cargarTopProductosGeneral(),
        this.cargarUltimasVentas(), this.cargarAlertas(),
      ]);
    },
  },

  async mounted() {
    this.isLoading = true;
    try {
      this.id_usuario = await solicitudes.solicitarUsuarioToken();
      await this.cargarKpis();
      this.refreshTimer = setInterval(() => this.refreshKpis(), 60000);
    } catch (e) {
      console.error('Error init CEO dashboard:', e);
    } finally {
      this.isLoading = false;
    }
  },

  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────── */
.ceo-dashboard {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* ── KPI Grid ───────────────────────────────────────────────── */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.kpi-card {
  background: #fff;
  border-radius: 10px;
  padding: 1.1rem 1.25rem 0.85rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: relative;
  border-left: 4px solid transparent;
  transition: box-shadow 0.2s;
}
.kpi-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.12); }

.kpi-primary { border-left-color: #16A34A; }
.kpi-warning { border-left-color: #D97706; }
.kpi-danger  { border-left-color: #DC2626; }

.kpi-icon {
  font-size: 1.4rem;
  color: #6b7280;
  margin-bottom: 0.15rem;
}
.kpi-primary .kpi-icon { color: #16A34A; }
.kpi-warning .kpi-icon { color: #D97706; }
.kpi-danger  .kpi-icon { color: #DC2626; }

.kpi-body { flex: 1; display: flex; flex-direction: column; gap: 3px; }
.kpi-label { font-size: 0.78rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.kpi-value { font-size: 1.5rem; font-weight: 700; color: #111827; font-variant-numeric: tabular-nums; }
.kpi-sub   { font-size: 0.78rem; color: #9ca3af; }

.kpi-badge {
  display: inline-flex; align-items: center; gap: 2px;
  font-size: 0.76rem; font-weight: 600;
  padding: 2px 8px; border-radius: 12px; width: fit-content;
}
.trend-up   { background: #d1fae5; color: #065f46; }
.trend-down { background: #fee2e2; color: #991b1b; }

.kpi-link {
  font-size: 0.78rem; font-weight: 600; color: #a38655;
  text-decoration: none; margin-top: 0.5rem;
  transition: color 0.2s;
}
.kpi-link:hover { color: #7c5c35; }

/* Caja badge */
.caja-badge { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 0.82rem; font-weight: 700; width: fit-content; }
.badge-green { background: #d1fae5; color: #065f46; }
.badge-red   { background: #fee2e2; color: #991b1b; }
.badge-grey  { background: #f3f4f6; color: #6b7280; }

/* Alert pills */
.alert-pills { display: flex; gap: 5px; flex-wrap: wrap; margin: 2px 0; }
.pill { font-size: 0.72rem; font-weight: 600; padding: 2px 7px; border-radius: 10px; }
.pill-ok   { background: #d1fae5; color: #065f46; }
.pill-warn { background: #fef3c7; color: #92400e; }
.pill-err  { background: #fee2e2; color: #991b1b; }
.stock-critico { color: #b45309; font-weight: 600; }
.stock-critico i { margin-right: 2px; }

/* ── Mid section ────────────────────────────────────────────── */
.mid-section {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1rem;
}

/* ── Panels ─────────────────────────────────────────────────── */
.panel {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  padding: 1rem 1.25rem;
}

.panel-header {
  font-size: 0.9rem; font-weight: 700; color: #374151;
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 0.85rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.top-col-label {
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Top 5 */
.top-list { display: flex; flex-direction: column; gap: 6px; }
.top-item {
  display: grid; grid-template-columns: 28px 1fr auto;
  align-items: center; gap: 8px;
  padding: 5px 8px; border-radius: 6px;
  background: #f9fafb; font-size: 0.85rem;
}
.rank         { font-weight: 700; color: #9ca3af; font-size: 0.78rem; }
.rank-gold    { color: #D97706; }
.rank-silver  { color: #6b7280; }
.rank-bronze  { color: #92400e; }
.prod-nombre { font-weight: 500; color: #1f2937; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.prod-uds { font-size: 0.76rem; color: #6b7280; white-space: nowrap; }

/* Chart */
.chart-panel { display: flex; flex-direction: column; }
.chart-wrap { flex: 1; min-height: 200px; position: relative; }

/* Sales table */
.sales-panel { display: flex; flex-direction: column; }
.table-wrap { overflow-x: auto; }
.sales-table {
  width: 100%; min-width: 520px; border-collapse: collapse;
  font-size: 0.87rem;
}
.sales-table th, .sales-table td {
  padding: 8px 10px; border: 1px solid #e5e7eb; text-align: left;
}
.sales-table th { background: #f9fafb; font-weight: 600; color: #374151; font-size: 0.8rem; }
.sales-table td { color: #374151; }
.sales-table tr:hover td { background: #f9fafb; }
.monto-col { font-weight: 600; font-variant-numeric: tabular-nums; }

.ver-todas-link {
  display: inline-block; margin-top: 0.75rem;
  font-size: 0.82rem; font-weight: 600; color: #a38655;
  text-decoration: none;
}
.ver-todas-link:hover { color: #7c5c35; }

/* No data */
.no-data {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  min-height: 80px; color: #9ca3af; gap: 0.4rem;
  font-size: 0.85rem;
}
.no-data i { font-size: 2rem; }

/* ── Dark Mode ───────────────────────────────────────────────── */
.dark .kpi-card,
.dark .panel { background: #2d2d2d; box-shadow: 0 2px 6px rgba(0,0,0,0.3); }

.dark .kpi-value { color: #f3f4f6; }
.dark .kpi-label { color: #9ca3af; }
.dark .kpi-sub   { color: #6b7280; }
.dark .kpi-icon  { color: #9ca3af; }
.dark .kpi-primary .kpi-icon { color: #4ade80; }
.dark .kpi-warning .kpi-icon { color: #fbbf24; }
.dark .kpi-danger  .kpi-icon { color: #f87171; }
.dark .kpi-link    { color: #c09d62; }
.dark .kpi-link:hover { color: #e8c98e; }

.dark .trend-up   { background: rgba(16,185,129,0.2); color: #6ee7b7; }
.dark .trend-down { background: rgba(220,38,38,0.2);  color: #fca5a5; }
.dark .badge-green { background: rgba(16,185,129,0.2); color: #6ee7b7; }
.dark .badge-red   { background: rgba(220,38,38,0.2);  color: #fca5a5; }
.dark .badge-grey  { background: rgba(107,114,128,0.2); color: #9ca3af; }
.dark .pill-ok     { background: rgba(16,185,129,0.2); color: #6ee7b7; }
.dark .pill-warn   { background: rgba(245,158,11,0.2); color: #fcd34d; }
.dark .pill-err    { background: rgba(220,38,38,0.2);  color: #fca5a5; }

.dark .panel-header { color: #e5e7eb; border-bottom-color: #383838; }
.dark .top-item { background: #383838; }
.dark .prod-nombre { color: #f3f4f6; }
.dark .rank, .dark .prod-uds { color: #9ca3af; }

.dark .sales-table th { background: #383838; color: #d1d5db; border-color: #4b5563; }
.dark .sales-table td { color: #d1d5db; border-color: #4b5563; }
.dark .sales-table tr:hover td { background: #383838; }
.dark .ver-todas-link { color: #c09d62; }
.dark .ver-todas-link:hover { color: #e8c98e; }
.dark .no-data { color: #6b7280; }

/* ── Responsive ─────────────────────────────────────────────── */

/* Laptops medianas — 3 cols KPI, sección mid apilada */
@media (max-width: 1100px) {
  .kpi-grid    { grid-template-columns: repeat(3, 1fr); }
  .mid-section { grid-template-columns: 1fr; }
  .chart-wrap  { min-height: 230px; }
}

/* Tablets */
@media (max-width: 768px) {
  .ceo-dashboard { padding: 14px; gap: 1rem; }
  .kpi-grid  { grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
  .kpi-card  { padding: 0.9rem 1rem 0.7rem; }
  .kpi-value { font-size: 1.3rem; }
  .chart-wrap { min-height: 210px; }
  .sales-table th,
  .sales-table td { padding: 7px 8px; font-size: 0.82rem; }
}

/* Móviles */
@media (max-width: 480px) {
  .ceo-dashboard { padding: 10px; gap: 0.7rem; }
  .kpi-grid  { grid-template-columns: repeat(2, 1fr); gap: 0.45rem; }
  .kpi-card  { padding: 0.7rem 0.75rem 0.55rem; gap: 0.15rem; }
  .kpi-value { font-size: 1.05rem; }
  .kpi-label { font-size: 0.68rem; }
  .kpi-icon  { font-size: 1.1rem; margin-bottom: 0.05rem; }
  .kpi-sub   { font-size: 0.7rem; }
  .kpi-link  { font-size: 0.7rem; margin-top: 0.35rem; }
  .kpi-badge { font-size: 0.66rem; padding: 1px 5px; }
  .caja-badge { font-size: 0.75rem; padding: 2px 7px; }
  .alert-pills { gap: 3px; }
  .pill      { font-size: 0.63rem; padding: 1px 4px; }
  .panel     { padding: 0.7rem 0.85rem; }
  .panel-header { font-size: 0.82rem; margin-bottom: 0.6rem; }
  .top-col-label { font-size: 0.64rem; }
  .top-item  { font-size: 0.78rem; padding: 4px 5px; gap: 6px; }
  .prod-nombre { font-size: 0.76rem; }
  .prod-uds  { font-size: 0.7rem; }
  .chart-wrap { min-height: 175px; }
  .sales-table th,
  .sales-table td { padding: 6px 5px; font-size: 0.75rem; }
  .ver-todas-link { font-size: 0.75rem; }
}

/* Móviles muy pequeños — 1 columna */
@media (max-width: 360px) {
  .kpi-grid  { grid-template-columns: 1fr; }
  .kpi-value { font-size: 1rem; }
  .kpi-card  { padding: 0.65rem 0.7rem 0.5rem; }
}

/* Landscape en móvil */
@media (max-height: 480px) and (orientation: landscape) {
  .ceo-dashboard { padding: 8px; gap: 0.6rem; }
  .kpi-grid { grid-template-columns: repeat(3, 1fr); gap: 0.4rem; }
  .kpi-card { padding: 0.55rem 0.7rem 0.45rem; }
  .kpi-value { font-size: 1rem; }
  .chart-wrap { min-height: 150px; }
}
</style>
